import React, { lazy, Suspense } from 'react';
import { IconContext } from '@phosphor-icons/react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
// Login e InitialSetup se mantienen eager: son el primer paint del flujo de
// entrada/auth y son ligeros, así evitamos un flash de spinner en el login.
import Login from '@/pages/Login';
import InitialSetup from '@/pages/InitialSetup';
import ProtectedRoute from '@/components/ProtectedRoute';
import FirstUseClientPicker from '@/components/FirstUseClientPicker';
import Layout from '@/components/Layout';
// Code-splitting: cada página se carga bajo demanda (React.lazy) para que el
// bundle inicial sea pequeño y la app/dashboard carguen rápido. Se resuelven
// dentro del <Suspense> de AnimatedRoutes.
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const NetworkGraph = lazy(() => import('@/pages/NetworkGraph'));
const Agents = lazy(() => import('@/pages/Agents'));
const AgentDetails = lazy(() => import('@/pages/AgentDetails'));
const AgentHealth = lazy(() => import('@/pages/AgentHealth'));
const Vulnerabilities = lazy(() => import('@/pages/Vulnerabilities'));
const SystemMetrics = lazy(() => import('@/pages/SystemMetrics'));
const Download = lazy(() => import('@/pages/Download'));
const ADAttacks = lazy(() => import('@/pages/ADAttacks'));
const DataLeaks = lazy(() => import('@/pages/DataLeaks'));
const DLP = lazy(() => import('@/pages/DLP'));
const NetworkScanner = lazy(() => import('@/pages/NetworkScanner'));
const Reports = lazy(() => import('@/pages/Reports'));
const AttackLibrary = lazy(() => import('@/pages/AttackLibrary'));
const AttackLibraryRuns = lazy(() => import('@/pages/AttackLibraryRuns'));
const AttackPacks = lazy(() => import('@/pages/AttackPacks'));
const AttackPathsPage = lazy(() => import('@/pages/AttackPaths'));
const SIEMIntegrations = lazy(() => import('@/pages/SIEMIntegrations'));
const ExternalSIEM = lazy(() => import('@/pages/ExternalSIEM'));
const Remediation = lazy(() => import('@/pages/Remediation'));
const SecurityRoadmap = lazy(() => import('@/pages/SecurityRoadmap'));
const ThreatModel = lazy(() => import('@/pages/ThreatModel'));
const Settings = lazy(() => import('@/pages/Settings'));
const UsersManagement = lazy(() => import('@/pages/UsersManagement'));
const Compliance = lazy(() => import('@/pages/Compliance'));
const Integrations = lazy(() => import('@/pages/Integrations'));
const AlertManagement = lazy(() => import('@/pages/AlertManagement'));
const AuditTrail = lazy(() => import('@/pages/AuditTrail'));
const ScheduledReports = lazy(() => import('@/pages/ScheduledReports'));
const EDRIntegration = lazy(() => import('@/pages/EDRIntegration'));
const ThreatIntelligence = lazy(() => import('@/pages/ThreatIntelligence'));
const DataRetention = lazy(() => import('@/pages/DataRetention'));
const IPFilterManagement = lazy(() => import('@/pages/IPFilterManagement'));
const AntivirusStatus = lazy(() => import('@/pages/AntivirusStatus'));
const Inventory = lazy(() => import('@/pages/Inventory'));
const SSOConfig = lazy(() => import('@/pages/SSOConfig'));
const LicenseManagement = lazy(() => import('@/pages/LicenseManagement'));
const AgentGroups = lazy(() => import('@/pages/AgentGroups'));
const ComplianceDrift = lazy(() => import('@/pages/ComplianceDrift'));
const MiniSIEM = lazy(() => import('@/pages/MiniSIEM'));
const EmailSecurity = lazy(() => import('@/pages/EmailSecurity'));
const IOCManagement = lazy(() => import('@/pages/IOCManagement'));
const PlatformConfig = lazy(() => import('@/pages/PlatformConfig'));
const DocumentationCenter = lazy(() => import('@/pages/DocumentationCenter'));
const LandingPage = lazy(() => import('@/pages/LandingPage'));
const LandingPyme = lazy(() => import('@/pages/LandingPyme'));
const Features = lazy(() => import('@/pages/Features'));
const IngestionStats = lazy(() => import('@/pages/IngestionStats'));
const CertificateManagement = lazy(() => import('@/pages/CertificateManagement'));
const SigmaRules = lazy(() => import('@/pages/SigmaRules'));
const YaraRules = lazy(() => import('@/pages/YaraRules'));
const CorrelationAlerts = lazy(() => import('@/pages/CorrelationAlerts'));
const ThreatMap = lazy(() => import('@/pages/ThreatMap'));
const MITREcoverage = lazy(() => import('@/pages/MITREcoverage'));
const BASDashboard = lazy(() => import('@/pages/BASDashboard'));
const AIMutation = lazy(() => import('@/pages/AIMutation'));
const CtiInbox = lazy(() => import('@/pages/CtiInbox'));
const Changelog = lazy(() => import('@/pages/Changelog'));
const ChangePassword = lazy(() => import('@/pages/ChangePassword'));
import { SidebarProvider } from '@/contexts/SidebarContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { ViewModeProvider } from '@/contexts/ViewModeContext';
import { ToastProvider } from '@/components/ToastProvider';
import { TooltipProvider } from '@/components/ui/tooltip';
import { LanguageProvider } from '@/i18n';
import { AuthProvider } from '@/contexts/AuthContext';
import { ClientProvider, useClient } from '@/contexts/ClientContext';
import ErrorBoundary from '@/components/ErrorBoundary';
import apiClient from '@/lib/axiosInstance';
import '@/App.css';

// Wrapper component for protected routes with layout.
// Incluye FirstUseClientPicker para usuarios con múltiples clients sin selección previa (C8).
const ProtectedWithLayout = ({ children, adminOnly = false, minRole = null }) => {
  const { needsClientSelection, bootstrapLoading } = useClient();

  // Mientras bootstrap carga, no mostrar el picker aún (evitar flash)
  if (bootstrapLoading) {
    return (
      <ProtectedRoute adminOnly={adminOnly} minRole={minRole}>
        <div className="min-h-screen bg-v4-bg flex items-center justify-center">
          <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-v4-line-2 border-r-transparent" />
        </div>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute adminOnly={adminOnly} minRole={minRole}>
      {needsClientSelection && <FirstUseClientPicker />}
      <Layout>{children}</Layout>
    </ProtectedRoute>
  );
};

// Fallback mostrado mientras se descarga el chunk de una página lazy.
const PageFallback = () => (
  <div className="min-h-screen bg-v4-bg flex items-center justify-center">
    <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-v4-line-2 border-r-transparent" />
  </div>
);

const AnimatedRoutes = () => {
  return (
    <Suspense fallback={<PageFallback />}>
    <Routes>
        {/* Public routes */}
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/pymes" element={<LandingPyme />} />
        <Route path="/features" element={<Features />} />
        <Route path="/login" element={<Login />} />
        {/* Cambio de contraseña forzado (#4): solo requiere JWT, sin minRole ni
            client picker. ProtectedRoute permite esta ruta aunque mustChangePassword
            esté activo (es justamente donde se limpia el flag). */}
        <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
        <Route path="/setup" element={<SetupRoute />} />
        {/* /tenant-selection eliminado (C8): reemplazado por ClientSwitcher topbar + FirstUseClientPicker */}
        <Route path="/tenant-selection" element={<Navigate to="/" replace />} />

        {/* Protected routes with persistent navigation */}
        <Route path="/" element={<ProtectedWithLayout><Dashboard /></ProtectedWithLayout>} />
        <Route path="/security-roadmap" element={<ProtectedWithLayout minRole="viewer"><SecurityRoadmap /></ProtectedWithLayout>} />
        <Route path="/threat-model" element={<ProtectedWithLayout minRole="viewer"><ThreatModel /></ProtectedWithLayout>} />
        <Route path="/threat-map" element={<ProtectedWithLayout><ThreatMap /></ProtectedWithLayout>} />
        <Route path="/network" element={<ProtectedWithLayout><NetworkGraph /></ProtectedWithLayout>} />
        <Route path="/network-scanner" element={<ProtectedWithLayout><NetworkScanner /></ProtectedWithLayout>} />
        <Route path="/agents" element={<ProtectedWithLayout><Agents /></ProtectedWithLayout>} />
        <Route path="/agents/:agentId" element={<ProtectedWithLayout><AgentDetails /></ProtectedWithLayout>} />
        <Route path="/agent-health" element={<ProtectedWithLayout><AgentHealth /></ProtectedWithLayout>} />
        <Route path="/vulnerabilities" element={<ProtectedWithLayout minRole="viewer"><Vulnerabilities /></ProtectedWithLayout>} />
        <Route path="/ad-attacks" element={<ProtectedWithLayout minRole="viewer"><ADAttacks /></ProtectedWithLayout>} />
        {/* Retirada T5 (2026-10-13): IncidentResponse (duplicaba Vanguard y no
            persistia nada) y HumanLayer (todo sintetico, Math.random) se han
            retirado. Candado: backend/tests/test_retirado_t5_pantallas.py */}
        <Route path="/data-leaks" element={<ProtectedWithLayout><DataLeaks /></ProtectedWithLayout>} />
        <Route path="/dlp" element={<ProtectedWithLayout><DLP /></ProtectedWithLayout>} />
        <Route path="/metrics" element={<ProtectedWithLayout><SystemMetrics /></ProtectedWithLayout>} />
        <Route path="/download" element={<ProtectedWithLayout><Download /></ProtectedWithLayout>} />
        <Route path="/reports" element={<ProtectedWithLayout minRole="viewer"><Reports /></ProtectedWithLayout>} />
        <Route path="/attack-simulation" element={<Navigate to="/attack-library" replace />} />
        <Route path="/attack-library" element={<ProtectedWithLayout minRole="viewer"><AttackLibrary /></ProtectedWithLayout>} />
        <Route path="/attack-library/runs" element={<ProtectedWithLayout><AttackLibraryRuns /></ProtectedWithLayout>} />
        <Route path="/packs" element={<ProtectedWithLayout><AttackPacks /></ProtectedWithLayout>} />
        {/* minRole viewer: su backend exige `require_soc_read`, así que sin él
            el rol `user` aterrizaba en una página llena de errores 403.
            Añadido 2026-11-05 al enlazar aquí desde el panel. */}
        <Route path="/attack-paths" element={<ProtectedWithLayout minRole="viewer"><AttackPathsPage /></ProtectedWithLayout>} />
        <Route path="/bas" element={<ProtectedWithLayout><BASDashboard /></ProtectedWithLayout>} />
        <Route path="/ai-mutation" element={<ProtectedWithLayout><AIMutation /></ProtectedWithLayout>} />
        <Route path="/cti-inbox" element={<ProtectedWithLayout><CtiInbox /></ProtectedWithLayout>} />
        <Route path="/siem-integrations" element={<ProtectedWithLayout><SIEMIntegrations /></ProtectedWithLayout>} />
        <Route path="/external-siem" element={<ProtectedWithLayout><ExternalSIEM /></ProtectedWithLayout>} />
        <Route path="/compliance" element={<ProtectedWithLayout minRole="viewer"><Compliance /></ProtectedWithLayout>} />
        <Route path="/integrations" element={<ProtectedWithLayout adminOnly><Integrations /></ProtectedWithLayout>} />
        <Route path="/alerts" element={<ProtectedWithLayout minRole="viewer"><AlertManagement /></ProtectedWithLayout>} />
        <Route path="/audit-trail" element={<ProtectedWithLayout adminOnly><AuditTrail /></ProtectedWithLayout>} />
        <Route path="/scheduled-reports" element={<ProtectedWithLayout><ScheduledReports /></ProtectedWithLayout>} />
        <Route path="/edr" element={<ProtectedWithLayout><EDRIntegration /></ProtectedWithLayout>} />
        <Route path="/threat-intel" element={<ProtectedWithLayout><ThreatIntelligence /></ProtectedWithLayout>} />
        {/* Retención de datos: la gestiona el PROVEEDOR, no el cliente. La política
            es global (auto_delete_old_data recorre TODOS los tenants con sesión de
            sistema), por eso el backend exige superadmin en /policy y /run
            (backend/routes/data_retention.py). Con `adminOnly` el administrador de
            un cliente entraba y todos los botones le devolvían 403. */}
        <Route path="/data-retention" element={<ProtectedWithLayout minRole="superadmin"><DataRetention /></ProtectedWithLayout>} />
        <Route path="/ip-filter" element={<ProtectedWithLayout adminOnly><IPFilterManagement /></ProtectedWithLayout>} />
        <Route path="/remediation" element={<ProtectedWithLayout><Remediation /></ProtectedWithLayout>} />
        <Route path="/settings" element={<ProtectedWithLayout adminOnly><Settings /></ProtectedWithLayout>} />
        <Route path="/platform-config" element={<ProtectedWithLayout adminOnly><PlatformConfig /></ProtectedWithLayout>} />
        <Route path="/users" element={<ProtectedWithLayout adminOnly><UsersManagement /></ProtectedWithLayout>} />
        {/* Retirada T5 (2026-10-13): la pantalla ApiDocs era una lista a mano de 16
            endpoints con un 25% de rutas inexistentes. La referencia viva es la que
            genera FastAPI: /api/docs (Swagger UI) y /api/openapi.json. */}
        <Route path="/antivirus" element={<ProtectedWithLayout><AntivirusStatus /></ProtectedWithLayout>} />
        <Route path="/inventory" element={<ProtectedWithLayout minRole="viewer"><Inventory /></ProtectedWithLayout>} />
        <Route path="/sso" element={<ProtectedWithLayout adminOnly><SSOConfig /></ProtectedWithLayout>} />
        <Route path="/license" element={<ProtectedWithLayout adminOnly><LicenseManagement /></ProtectedWithLayout>} />
        <Route path="/agent-groups" element={<ProtectedWithLayout><AgentGroups /></ProtectedWithLayout>} />
        <Route path="/compliance-drift" element={<ProtectedWithLayout><ComplianceDrift /></ProtectedWithLayout>} />
        <Route path="/mini-siem" element={<ProtectedWithLayout minRole="viewer"><MiniSIEM /></ProtectedWithLayout>} />
        <Route path="/email-security" element={<ProtectedWithLayout><EmailSecurity /></ProtectedWithLayout>} />
        <Route path="/ioc-management" element={<ProtectedWithLayout><IOCManagement /></ProtectedWithLayout>} />
        <Route path="/documentation" element={<ProtectedWithLayout><DocumentationCenter /></ProtectedWithLayout>} />
        <Route path="/changelog" element={<ProtectedWithLayout><Changelog /></ProtectedWithLayout>} />
        <Route path="/sigma-rules" element={<ProtectedWithLayout minRole="viewer"><SigmaRules /></ProtectedWithLayout>} />
        <Route path="/yara-rules" element={<ProtectedWithLayout minRole="viewer"><YaraRules /></ProtectedWithLayout>} />
        <Route path="/correlation" element={<ProtectedWithLayout minRole="viewer"><CorrelationAlerts /></ProtectedWithLayout>} />
        <Route path="/mitre-coverage" element={<ProtectedWithLayout minRole="viewer"><MITREcoverage /></ProtectedWithLayout>} />
        <Route path="/ingestion-stats" element={<ProtectedWithLayout><IngestionStats /></ProtectedWithLayout>} />
        <Route path="/certificate-management" element={<ProtectedWithLayout><CertificateManagement /></ProtectedWithLayout>} />
        {/* Legacy/orphan route redirects (bookmarks viejos) → evitar pantalla en blanco */}
        <Route path="/sigma" element={<Navigate to="/sigma-rules" replace />} />
        <Route path="/yara" element={<Navigate to="/yara-rules" replace />} />
        <Route path="/ioc" element={<Navigate to="/ioc-management" replace />} />
        <Route path="/certificates" element={<Navigate to="/certificate-management" replace />} />
        <Route path="/threat-intelligence" element={<Navigate to="/threat-intel" replace />} />
        <Route path="/docs" element={<Navigate to="/documentation" replace />} />
        {/* item12: /dashboard had no route (the dashboard lives at "/"), so hitting it
            directly rendered a blank page ("No routes matched location /dashboard").
            Alias it and add a catch-all so any unknown path lands on the dashboard. */}
        <Route path="/dashboard" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
};

/**
 * SetupRoute - Ruta condicional para /setup
 *
 * - Primera instalación (no hay usuarios): acceso público (setup requerido)
 * - Post-primer-setup: redirige a /login si no está autenticado
 *
 * InitialSetup.js ya verifica internamente setup-status y redirige si no se necesita,
 * pero este wrapper añade protección extra: si el usuario no está autenticado Y
 * el setup no es requerido, ni siquiera carga el componente.
 */
const SetupRoute = () => {
  const [setupRequired, setSetupRequired] = React.useState(null);

  React.useEffect(() => {
    const checkSetup = async () => {
      try {
        const response = await apiClient.get('/auth/setup-status', { timeout: 5000 });
        setSetupRequired(response.data.setup_required);
      } catch {
        // On error, assume setup not required (safer)
        setSetupRequired(false);
      }
    };
    checkSetup();
  }, []);

  // Loading while checking
  if (setupRequired === null) {
    return (
      <div className="min-h-screen bg-v4-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-v4-line-2 border-r-transparent"></div>
          <p className="text-v4-dim">Verificando estado del sistema...</p>
        </div>
      </div>
    );
  }

  // Setup is required (first install, no users) → allow public access
  if (setupRequired) {
    return <InitialSetup />;
  }

  // Setup already done → require authentication
  return (
    <ProtectedRoute>
      <InitialSetup />
    </ProtectedRoute>
  );
};

function App() {
  return (
    <IconContext.Provider value={{ size: 20, weight: 'duotone', mirrored: false }}>
    <LanguageProvider>
    <ThemeProvider>
    <AuthProvider>
    <ClientProvider>
    <ViewModeProvider>
    <TooltipProvider delayDuration={300}>
    <SidebarProvider>
      <ToastProvider>
      <div className="App">
        <ErrorBoundary>
        <BrowserRouter>
          <AnimatedRoutes />
        </BrowserRouter>
        </ErrorBoundary>
      </div>
      </ToastProvider>
    </SidebarProvider>
    </TooltipProvider>
    </ViewModeProvider>
    </ClientProvider>
    </AuthProvider>
    </ThemeProvider>
    </LanguageProvider>
    </IconContext.Provider>
  );
}

export default App;